import { SEO } from "@/components/SEO";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";

const MAX_UTOPIA = 120;

export default function Mapa() {
  const [utopias, setUtopias] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", ciudad: "", utopia: "", email: "" });
  const [sending, setSending] = useState(false);
  const [newStar, setNewStar] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = () => api.get("/utopias").then(({ data }) => setUtopias(data)).catch(() => {});

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      const { data } = await api.post("/utopias", form);
      setNewStar(data);
      setUtopias((prev) => [data, ...prev]);
      setForm({ nombre: "", ciudad: "", utopia: "", email: "" });
      toast.success("Tu utopía ya brilla en la constelación.");
      setTimeout(() => { setOpen(false); setTimeout(() => setNewStar(null), 6000); }, 1500);
    } catch { toast.error("No pudimos registrar tu utopía."); } finally { setSending(false); }
  };

  return (
    <div className="constellation-bg text-cometa-crema min-h-screen relative overflow-hidden">
      <SEO title="Constelación de Utopías" description="Cada utopía es una estrella. Comparte la tuya y mírala brillar en la constelación viva de Dra. Cometa junto a cientos de personas mirando al mismo cielo." />
      {/* Estrellas de fondo decorativas */}
      {[...Array(80)].map((_, i) => {
        const size = Math.random() * 2.5 + 0.5;
        return <span key={`bg-${i}`} className="absolute rounded-full bg-white/60 animate-twinkle" style={{ top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, width: `${size}px`, height: `${size}px`, animationDelay: `${Math.random() * 3}s` }} />;
      })}

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 lg:py-28 text-center">
        <p className="font-script text-4xl md:text-5xl text-cometa-dorado mb-3">Constelación de Utopías</p>
        <h1 className="text-4xl sm:text-5xl md:text-7xl leading-[0.95] mb-6">Cada utopía es <em className="text-cometa-celeste">una estrella.</em></h1>
        <p className="max-w-2xl mx-auto text-cometa-crema/85 text-lg mb-10">
          Comparte la tuya. Se convertirá en un punto de luz en esta constelación viva y podrás encontrarla siempre que quieras mirar al cielo.
        </p>
        <button data-testid="mapa-abrir-form" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-cometa-dorado text-cometa-tinta px-8 py-4 rounded-full font-semibold hover:bg-cometa-carmesi hover:text-white transition">
          Comparte tu utopía <Sparkles size={16} />
        </button>
      </div>

      {/* Constelación interactiva */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-20">
        <div className="relative aspect-[16/10] rounded-3xl bg-black/30 border border-cometa-crema/10 backdrop-blur overflow-hidden" data-testid="mapa-constelacion">
          {utopias.map((u, i) => (
            <button
              key={u.id || i}
              onMouseEnter={() => setHover(u)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(u)}
              className="absolute group"
              style={{ top: `${(u.y || 0.5) * 100}%`, left: `${(u.x || 0.5) * 100}%`, transform: 'translate(-50%,-50%)' }}
              aria-label={`Utopía de ${u.nombre}`}
              data-testid={`estrella-${i}`}
            >
              <span className={`block rounded-full bg-cometa-dorado shadow-[0_0_20px_rgba(255,188,1,0.7)] ${newStar && newStar.id === u.id ? "animate-ping" : "animate-twinkle"}`} style={{ width: `${(Math.random() * 4) + 4}px`, height: `${(Math.random() * 4) + 4}px` }} />
            </button>
          ))}
          {hover && (
            <div className="pointer-events-none absolute bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-80 bg-cometa-tinta/90 backdrop-blur border border-cometa-crema/20 rounded-2xl p-5">
              <p className="text-cometa-dorado font-script text-2xl">{hover.nombre}</p>
              {hover.ciudad && <p className="text-cometa-crema/60 text-xs tracking-widest">{hover.ciudad.toUpperCase()}</p>}
              <p className="mt-2 text-cometa-crema/90">{hover.utopia}</p>
            </div>
          )}
          {utopias.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-cometa-crema/50 italic">El cielo espera tu primera estrella.</p>
            </div>
          )}
        </div>
        <p className="text-center text-cometa-crema/50 text-sm mt-4">{utopias.length} {utopias.length === 1 ? "utopía compartida" : "utopías compartidas"}</p>
      </div>

      {/* Momento WOW: modal + animación estrella nueva */}
      <AnimatePresence>
        {newStar && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center">
            <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 2, 1], opacity: [0, 1, 0.8] }} transition={{ duration: 2 }} className="w-6 h-6 rounded-full bg-cometa-dorado shadow-[0_0_80px_30px_rgba(255,188,1,0.6)]" />
          </motion.div>
        )}
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} className="w-full max-w-lg bg-cometa-crema text-cometa-tinta rounded-3xl p-8 relative">
              <button onClick={() => setOpen(false)} className="absolute top-4 right-4 p-1 rounded-full hover:bg-cometa-tinta/10" aria-label="Cerrar" data-testid="mapa-cerrar"><X size={20} /></button>
              <p className="font-script text-4xl text-cometa-carmesi mb-1">¿Cuál es tu utopía?</p>
              <p className="text-cometa-tinta/60 text-sm mb-6">Compártela con nosotros. Aparecerá en la constelación.</p>
              <form onSubmit={submit} className="space-y-3">
                <input required placeholder="Tu nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full border border-cometa-tinta/20 rounded-xl px-5 py-3 focus:outline-none focus:border-cometa-marino" data-testid="mapa-form-nombre" />
                <input placeholder="Ciudad (opcional)" value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} className="w-full border border-cometa-tinta/20 rounded-xl px-5 py-3 focus:outline-none focus:border-cometa-marino" data-testid="mapa-form-ciudad" />
                <div>
                  <textarea required rows={2} maxLength={MAX_UTOPIA} placeholder="Mi utopía es... (breve, para que quepa en tu estrella)" value={form.utopia} onChange={(e) => setForm({ ...form, utopia: e.target.value })} className="w-full border border-cometa-tinta/20 rounded-xl px-5 py-3 focus:outline-none focus:border-cometa-marino" data-testid="mapa-form-utopia" />
                  <p className="text-right text-xs text-cometa-tinta/50 mt-1" data-testid="mapa-form-contador">{form.utopia.length}/{MAX_UTOPIA}</p>
                </div>
                <input type="email" placeholder="Correo (opcional, para invitarte a nuestra comunidad)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-cometa-tinta/20 rounded-xl px-5 py-3 focus:outline-none focus:border-cometa-marino" data-testid="mapa-form-email" />
                <button disabled={sending} data-testid="mapa-form-submit" className="w-full bg-cometa-marino text-cometa-crema py-4 rounded-full font-semibold hover:bg-cometa-tinta transition disabled:opacity-60">
                  {sending ? "Encendiendo tu estrella..." : "Enciende mi estrella"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
